import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import Pricing from './Pricing';
import CallToAction from './CallToAction';

const Ecommerce = () => {
  useEffect(() => {
    window.scrollTo(0, 0);

    // Add UET script for Bing Ads tracking
    const script = document.createElement('script');
    script.innerHTML = `
      (function(w,d,t,r,u){var f,n,i;w[u]=w[u]||[],f=function(){var o={ti:"295022966"};o.q=w[u],w[u]=new UET(o),w[u].push("pageLoad")},n=d.createElement(t),n.src=r,n.async=1,n.onload=n.onreadystatechange=function(){var s=this.readyState;s&&s!=="loaded"&&s!=="complete"||(f(),n.onload=n.onreadystatechange=null)},i=d.getElementsByTagName(t)[0],i.parentNode.insertBefore(n,i)})(window,document,"script","//bat.bing.com/bat.js","uetq");
    `;
    document.head.appendChild(script);

    // Cleanup function to remove script when component unmounts
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>E-commerce Video Marketing - Sales Intercept</title>
        <meta name="description" content="Boost e-commerce sales with engaging vertical video. Turn your store into a TikTok-like experience, increasing conversions, time on site, and reducing bounce rates. Try free." />
      </Helmet>
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 lg:pt-40 pb-12 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6">
                Turn Your Store Into a <span className="text-purple-si">TikTok Experience</span>
              </h1>
              <p className="text-xl text-gray-700 mb-8">
                Add swipeable, vertical video to your Shopify store. Drive more sales with the content you already have.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="https://dashboard.salesintercept.com/signup" 
                   className="btn btn-primary">
                  Start Free Trial
                </a>
              </div>
              <p className="text-sm text-gray-500 italic mt-4">
                One-time code embed. No technical skills or credit card required.
              </p>
            </div>
            <div className="lg:w-1/2 relative">
              <div className="bg-white p-8 rounded-xl shadow-2xl">
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-4 bg-purple-si/10 rounded-lg">
                    <div className="text-3xl font-bold text-purple-si mb-2">18-20%</div>
                    <div className="text-sm">Lift in Conversion Rates</div>
                  </div>
                  <div className="text-center p-4 bg-orange-dark-si/10 rounded-lg">
                    <div className="text-3xl font-bold text-orange-dark-si mb-2">76%</div>
                    <div className="text-sm">Increase in Time on Site</div>
                  </div>
                  <div className="text-center p-4 bg-aqua-si/10 rounded-lg">
                    <div className="text-3xl font-bold text-aqua-si mb-2">31%</div>
                    <div className="text-sm">Drop in Bounce Rates</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Featured Client - Aquatek Coatings */}
      <section className="bg-white py-12">
        <div className="container mx-auto">
          <div className="bg-gradient-to-br from-purple-si/20 to-aqua-si/20 rounded-2xl p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="md:w-1/3">
                <img 
                  src="https://static.wixstatic.com/media/1b608d_aba8b51e9c374665ac1fd11dd7ff523e~mv2.png/v1/fill/w_488,h_276,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/full_trimmed_transparent_base.png"
                  alt="Aquatek Coatings Logo"
                  className="w-full h-auto"
                />
              </div>
              <div className="md:w-2/3">
                <h2 className="text-2xl md:text-3xl font-bold mb-4">
                  Featured Client: <span className="text-purple-si">Aquatek Coatings</span>
                </h2>
                <p className="text-gray-700 mb-6">
                  See Sales Intercept in action on Aquatek Coatings' website, enhancing their e-commerce 
                  and lead generation experience through engaging video interactions.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <a 
                    href="https://www.aquatekcoatings.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-all hover:scale-105 cursor-pointer"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-bold">Homepage</h3>
                      <span className="text-purple-si text-sm">↗</span>
                    </div>
                    <p className="text-sm text-gray-600">Experience the main website integration</p>
                    <div className="mt-2 text-purple-si text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      Click to view →
                    </div>
                  </a>
                  <a 
                    href="https://www.aquatekcoatings.com/apply"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-all hover:scale-105 cursor-pointer"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-bold">Lead Generation</h3>
                      <span className="text-purple-si text-sm">↗</span>
                    </div>
                    <p className="text-sm text-gray-600">See how it drives conversions</p>
                    <div className="mt-2 text-purple-si text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      Click to view →
                    </div>
                  </a>
                  <a 
                    href="https://www.aquatekcoatings.com/product-page/clarity-window-glass-coating"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-all hover:scale-105 cursor-pointer"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-bold">Product Catalog</h3>
                      <span className="text-purple-si text-sm">↗</span>
                    </div>
                    <p className="text-sm text-gray-600">View the e-commerce integration</p>
                    <div className="mt-2 text-purple-si text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      Click to view →
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Trusted by <span className="text-purple-si">Growing Businesses</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Don't just take our word for it. Here's what our customers are saying about 
              Sales Intercept.
            </p>
          </div>

          <div className="max-w-2xl mx-auto space-y-6">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="mb-4">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 italic mb-4">"We saw a 43% increase in conversion rates after adding Sales Intercept to our website. The widget grabs attention immediately and keeps visitors engaged longer. The ROI has been incredible."</p>
              </div>
              <div>
                <h4 className="font-bold">Mike Cardenas</h4>
                <p className="text-sm text-gray-500">E-commerce Manager</p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="mb-4">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 italic mb-4">"What I love about Sales Intercept is how it helps us connect with visitors on a personal level without requiring more staff. The analytics provide valuable insights into what resonates with our audience."</p>
              </div>
              <div>
                <h4 className="font-bold">Rob Stein</h4>
                <p className="text-sm text-gray-500">CEO, Donald Ross Sportwear</p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="mb-4">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 italic mb-4">"Sales Intercept has made our website far more personal and interactive. It's helped us stand out from all the competition. It's very easy to install and intuitive to use plus the customer service is 5 star. I cannot rave about this product enough."</p>
              </div>
              <div>
                <h4 className="font-bold">Connie Newhouse</h4>
                <p className="text-sm text-gray-500">Marketing Director</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto max-w-3xl">
          <div className="space-y-12">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-6">The Content You Already Have Works</h2>
              <p className="text-gray-700 mb-12">
                If you're running paid ads or posting on Instagram, TikTok, or YouTube Shorts, you already have the perfect content for your website.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-bold mb-2">UGC & Reviews</h3>
                  <p className="text-sm text-gray-600">Authentic customer testimonials and product reviews</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-bold mb-2">Brand Stories</h3>
                  <p className="text-sm text-gray-600">Share your values and founder journey</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-bold mb-2">Promos</h3>
                  <p className="text-sm text-gray-600">Launch new products and special offers</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-bold mb-2">Tutorials</h3>
                  <p className="text-sm text-gray-600">Product demos and how-to guides</p>
                </div>
              </div>
            </div>

            <div className="flex justify-center mb-12">
              <img 
                src="https://media.salesintercept.com/shopify-partners.svg"
                alt="Shopify Partners"
                className="h-16"
              />
            </div>

            <div className="text-center">
              <h2 className="text-3xl font-bold mb-6">Built for Shopify Teams</h2>
              <p className="text-gray-700 mb-8">
                No dev time required. SalesIntercept integrates seamlessly with Shopify, supports vertical video, and includes interactive features that keep your audience engaged—without slowing your site down.
              </p>
              <div className="bg-purple-si/10 p-6 rounded-lg">
                <h3 className="font-bold text-purple-si mb-2">Plug-and-Play Installation</h3>
                <p>Add to your store in minutes with our simple setup process.</p>
              </div>
            </div>

            {/* Performance Section */}
            <div className="mt-16 text-center">
              <h2 className="text-3xl font-bold mb-6">Lightning Fast Performance</h2>
              <div className="flex flex-col md:flex-row gap-8 items-center">
                <div className="md:w-1/2">
                  <img 
                    src="https://media.salesintercept.com/website-performance.jpg"
                    alt="Performance Chart showing fast loading times"
                    className="w-full h-auto rounded-lg"
                    loading="lazy"
                  />
                </div>
                <div className="md:w-1/2 text-left">
                  <h3 className="text-xl font-bold mb-4">Zero Impact on Page Speed</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                        <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                        </svg>
                      </div>
                      <span>Asynchronous loading - doesn't block page rendering</span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                        <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                        </svg>
                      </div>
                      <span>Optimized video delivery through global CDN</span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                        <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                        </svg>
                      </div>
                      <span>Lazy loading - videos load only when needed</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Additional Benefits Section */}
            <div className="mt-16 space-y-16">
              {/* Unlimited Impressions */}
              <div className="text-center">
                <h2 className="text-3xl font-bold mb-6">Unlimited Video Impressions</h2>
                <div className="flex flex-col md:flex-row gap-8 items-center">
                  <div className="md:w-1/2">
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-xl border border-green-100">
                      <div className="text-4xl font-bold text-green-600 mb-4">∞</div>
                      <h3 className="text-xl font-bold mb-4">Predictable Pricing</h3>
                      <p className="text-gray-700 mb-4">
                        No sliding scales, no caps, no surprises. Show your videos to as many visitors as you want 
                        without worrying about usage limits or escalating costs.
                      </p>
                      <ul className="space-y-2 text-left">
                        <li className="flex items-start">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                            </svg>
                          </div>
                          <span>Unlimited video views per month</span>
                        </li>
                        <li className="flex items-start">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                            </svg>
                          </div>
                          <span>Fixed monthly pricing</span>
                        </li>
                        <li className="flex items-start">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                            </svg>
                          </div>
                          <span>Scale without cost anxiety</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="md:w-1/2">
                    <div className="bg-gradient-to-br from-red-50 to-orange-50 p-8 rounded-xl border border-red-100">
                      <h3 className="text-xl font-bold mb-4 text-red-700">Other Platforms</h3>
                      <ul className="space-y-3 text-gray-700">
                        <li className="flex items-start">
                          <span className="text-red-500 mr-3 mt-1">✗</span>
                          <span>Pay per impression or view</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-red-500 mr-3 mt-1">✗</span>
                          <span>Costs increase with success</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-red-500 mr-3 mt-1">✗</span>
                          <span>Usage caps and overages</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-red-500 mr-3 mt-1">✗</span>
                          <span>Unpredictable monthly bills</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dwell Time */}
              <div className="text-center">
                <h2 className="text-3xl font-bold mb-6">Increase Dwell Time & SEO Rankings</h2>
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-xl border border-blue-100 max-w-4xl mx-auto">
                  <div className="grid md:grid-cols-3 gap-8 mb-8">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-blue-600 mb-2">+2.8x</div>
                      <div className="text-sm font-medium">Longer Time on Site</div>
                    </div>
                    <div className="text-center">
                      <div className="text-4xl font-bold text-purple-600 mb-2">-31%</div>
                      <div className="text-sm font-medium">Bounce Rate Reduction</div>
                    </div>
                    <div className="text-center">
                      <div className="text-4xl font-bold text-green-600 mb-2">↑</div>
                      <div className="text-sm font-medium">SEO Rankings</div>
                    </div>
                  </div>
                  <p className="text-gray-700 text-lg">
                    Video content naturally keeps visitors engaged longer. Google's algorithm rewards sites with 
                    higher dwell time, leading to better search rankings and more organic traffic to your store.
                  </p>
                </div>
              </div>

              {/* UGC Extension */}
              <div className="text-center">
                <h2 className="text-3xl font-bold mb-6">Extend Your UGC & Influencer Investment</h2>
                <div className="flex flex-col md:flex-row gap-8 items-center">
                  <div className="md:w-1/2 text-left">
                    <h3 className="text-xl font-bold mb-4">Don't Let Your Best Content Die in Ads</h3>
                    <p className="text-gray-700 mb-6">
                      You're already investing in user-generated content and influencer partnerships. 
                      Why limit that content to just ads and social posts? Bring it to your website 
                      where it can work 24/7 to convert visitors.
                    </p>
                    <ul className="space-y-4">
                      <li className="flex items-start">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-0.5">
                          <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                          </svg>
                        </div>
                        <span><strong>Repurpose UGC:</strong> Turn customer testimonials into website widgets</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-0.5">
                          <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                          </svg>
                        </div>
                        <span><strong>White-label Influencer Content:</strong> Showcase creator videos on your site</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-0.5">
                          <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                          </svg>
                        </div>
                        <span><strong>Maximize ROI:</strong> Get more value from your content investments</span>
                      </li>
                    </ul>
                  </div>
                  <div className="md:w-1/2">
                    <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-6 rounded-xl border border-orange-100">
                      <h4 className="font-bold mb-4">Content Journey</h4>
                      <div className="space-y-3">
                        <div className="flex items-center">
                          <span className="text-2xl mr-3">📱</span>
                          <span className="text-sm">Create UGC/Influencer content</span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-2xl mr-3">📺</span>
                          <span className="text-sm">Run as paid ads</span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-2xl mr-3">🌐</span>
                          <span className="text-sm font-bold text-orange-600">Add to website with Sales Intercept</span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-2xl mr-3">💰</span>
                          <span className="text-sm">Convert website visitors 24/7</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Behavioral Targeting */}
              <div className="text-center">
                <h2 className="text-3xl font-bold mb-6">Smart Behavioral Targeting</h2>
                <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-8 rounded-xl border border-purple-100 max-w-4xl mx-auto">
                  <p className="text-lg text-gray-700 mb-8">
                    Trigger videos based on real customer behavior. Show the right message at the perfect moment 
                    to maximize conversions and reduce cart abandonment.
                  </p>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-white p-6 rounded-lg shadow-md">
                      <h3 className="font-bold mb-4 flex items-center">
                        <span className="text-2xl mr-3">🛒</span>
                        Add to Cart Events
                      </h3>
                      <p className="text-gray-600 text-sm mb-3">
                        Show testimonials or urgency videos when customers add items to cart
                      </p>
                      <div className="text-purple-600 font-medium text-sm">
                        "Don't wait - only 3 left in stock!"
                      </div>
                    </div>
                    <div className="bg-white p-6 rounded-lg shadow-md">
                      <h3 className="font-bold mb-4 flex items-center">
                        <span className="text-2xl mr-3">💳</span>
                        Checkout Abandonment
                      </h3>
                      <p className="text-gray-600 text-sm mb-3">
                        Trigger discount or guarantee videos when users hesitate at checkout
                      </p>
                      <div className="text-purple-600 font-medium text-sm">
                        "Free shipping + 30-day returns!"
                      </div>
                    </div>
                    <div className="bg-white p-6 rounded-lg shadow-md">
                      <h3 className="font-bold mb-4 flex items-center">
                        <span className="text-2xl mr-3">⏱️</span>
                        Time on Page
                      </h3>
                      <p className="text-gray-600 text-sm mb-3">
                        Show product demos after visitors spend time browsing
                      </p>
                      <div className="text-purple-600 font-medium text-sm">
                        "See it in action!"
                      </div>
                    </div>
                    <div className="bg-white p-6 rounded-lg shadow-md">
                      <h3 className="font-bold mb-4 flex items-center">
                        <span className="text-2xl mr-3">👥</span>
                        Visitor Segmentation
                      </h3>
                      <p className="text-gray-600 text-sm mb-3">
                        Target new/returning visitors with videos/offers
                      </p>
                      <div className="text-purple-600 font-medium text-sm">
                        "Welcome back! Here's your personalized offer"
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Pricing />
      <CallToAction />
    </div>
    </>
  );
};

export default Ecommerce;